import React, { useState, useEffect } from "react";
import Head from "next/head";
import LoginRegistrationLayout from "components/LoginRegistrationLayout";
import SmallBusinessCard from "components/SmallBusinessCard";
import { useRouter } from "next/router";
import Button from "components/Button";
import useVendorClient from "hooks/use-vendor-client";
import { BASE_API_URL } from "lib/constants";
import LottieSpinner from "components/Lottie";
import Animation from "public/lotties/new-spinner.json";
import styled from "styled-components";
import ConditionalNavWrapper from "components/ConditionalNavWrapper";
import PropTypes from "prop-types";
import TextAreaInput from "fields/TextAreaInput";
import { Field } from "react-final-form";
import PageQuery from "components/PageQuery";
import tempId from "lib/tempId";
import useVendorUser from "hooks/use-vendor-user";

const LottieWrapper = styled.div`
  width: 6rem;
  width: 6rem;
  margin: auto;
`;

const BusinessCardWrapper = styled.div`
  padding: 15px;
  border: 1px solid ${({ theme }) => theme.themeColors.blue4};
  box-sizing: border-box;
  box-shadow: 0px 2px 6px rgba(138, 168, 181, 0.1253);
  border-radius: 12px;
`;

const LightParagraph = styled.p`
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  font-size: 1rem;
  font-weight: 400;
  line-height: 21px;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.themeColors.danger};
  width: 40rem;
  margin: auto;
`;

const Header2 = styled.h2`
  font-size: ${({ theme }) => {
    return theme.fontSize(5);
  }};
`;

const ClaimViewContainer = styled.div`
  margin-bottom: 2rem;
  ${({ theme }) => theme.mq.md`
    margin-bottom: none;
  `}
`;

const ClaimView = ({onClick, submitted, vendorBusiness, email }) => {
  const hasIntegrations = vendorBusiness?.include?.vendorBusinessIntegrations?.data?.length > 0;
  const hasInvite = vendorBusiness?.include?.vendorUserInvites?.data[0]?.attributes.email === email;
  if (hasIntegrations && !hasInvite) {
   return (
     <>
       <BusinessCardWrapper>
         <SmallBusinessCard vendorBusiness={vendorBusiness} />
       </BusinessCardWrapper>
       <br />
       <p>This business has already been claimed</p>
       <LightParagraph>
         If you believe this is your business please contact our support team.
       </LightParagraph>
     </>
   )
  }
  return (
    <>
      <Header2>Claim this Business</Header2>
      <BusinessCardWrapper>
        <SmallBusinessCard vendorBusiness={vendorBusiness} />
      </BusinessCardWrapper>
      <br />
      <LightParagraph>
        Are you sure you want to claim this business? This action cannot be
        undone.
      </LightParagraph>
      {!submitted ? (
        <Button onClick={onClick}>
          Continue
        </Button>
      ) : null}
    </>
  );
};

const AlreadyClaimedView = ({ vendorBusiness }) => {
  const [submitted, setSubmitted] = useState(false);
  const vendorUser = useVendorUser();
  const { push } = useRouter();

  return (
    <PageQuery
      userType="vendor"
      query={{
        ownershipDisputes: {
          writeOnly: true,
          id: tempId("dispute"),
          fields: ["notes"],
          onCreate: async () => {
            setSubmitted(true);
          },
        },
      }}
    >
      {({ handleSubmit }) => {
        return (
          <div>
            {submitted ? (
              <div>
                Thank you for your submission. Our team will review and be in
                contact.
                <br />
                <br />
                <br />
                <Button
                  onClick={() => {
                    push("/");
                  }}
                >
                  Back to Home
                </Button>
              </div>
            ) : (
              <div>
                <Header2>This business has already been claimed</Header2>
                <p>
                  This business has already been claimed by another user. If you
                  think this is someone else who is not authorized to manage
                  this business page you can report this business to our support
                  team.
                </p>
                <br />
                <form>
                  <Field
                    component={TextAreaInput}
                    inputLimit={200}
                    placeholder="Please let us know any specific details about the issue (if needed)"
                    name="ownershipDisputes.attributes.notes"
                  />
                  <Field
                    component="input"
                    type="hidden"
                    initialValue={vendorUser?.id}
                    name="ownershipDisputes.attributes.vendorUserId"
                  />
                  <Field
                    component="input"
                    type="hidden"
                    initialValue={vendorBusiness?.id}
                    name="ownershipDisputes.attributes.vendorBusinessId"
                  />
                  <br />
                  <Button onClick={handleSubmit}>Submit</Button>
                </form>
              </div>
            )}
          </div>
        );
      }}
    </PageQuery>
  );
};

function ExistingBusinessRedirect() {
  const [vendorBusiness, setVendorBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const vendorUser = useVendorUser();

  const {
    query: { bid },
    push,
  } = useRouter();
  const vendorClient = useVendorClient();

  useEffect(() => {
    const dataParams = {
      data: {
        data: {
          vendorBusiness: {
            id: bid,
            fields: [
              "businessName",
              "claimedDate",
              "formattedAddress",
              "vendorUserId",
            ],
            include: {
              vendorUserInvites: {
                readOnly: true,
                fields: ["email"],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                  email: vendorUser.email
                },
              },
              vendorBusinessIntegrations: {
                readOnly: true,
                fields: ["integrationID"],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                },
              },
              vendorServices: {
                readOnly: true,
                fields: ["name"],
                filter: {
                  vendorBusinessId: { fromContext: "vendorBusiness.id" },
                },
              },
            },
          },
        },
      },
    };
    vendorClient
      .request({
        method: "get",
        url: `${BASE_API_URL}/deep-unrest/read`,
        params: dataParams,
      })
      .then(({ data }) => {
        setVendorBusiness(data.vendorBusiness);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setError(
          "There appears to have been an error. Please reload page and try again."
        );
      });
  }, []);

  const continueHandler = () => {
    setLoading(true);
    setSubmitted(true);
    const payload = { vendor_business_id: bid };
    return vendorClient
      .post(`${BASE_API_URL}/claims/claim-business`, payload)
      .then(() => {
        vendorClient.validateToken().then(() => {
          push(`/provider/${bid}/dashboard/setup`);
        });
        setLoading(false);
      })
      .catch((err) => {
        const res = err.response;
        const errorDetail = res?.data?.errors?.[0]?.detail;
        let message =
          "There appears to have been an error. Please retry or contact support.";
        if (errorDetail) {
          message = `${errorDetail}`;
        }

        if (res && res.status === 401) {
          message = "Please login";
        }
        setLoading(false);
        setError(message);
      });
  };
  if (error) {
    return (
      <ConditionalNavWrapper>
        <Head>
          <title>GoodFriend | Business Search</title>
        </Head>
        <ErrorText>{error}</ErrorText>
      </ConditionalNavWrapper>
    )
  }
  if (loading) {
    return (
      <ConditionalNavWrapper>
        <Head>
          <title>GoodFriend | Business Search</title>
        </Head>
        <LottieWrapper>
          <LottieSpinner animation={Animation} />
        </LottieWrapper>
      </ConditionalNavWrapper>
    );
  }
  return (
    <ConditionalNavWrapper>
      <Head>
        <title>GoodFriend | Business Search</title>
      </Head>
      <LoginRegistrationLayout
        image="/dog_on_pink_background.jpg"
        altText="dog on pink background"
      >
        {!!vendorBusiness?.attributes?.claimedDate && !!vendorBusiness?.attributes?.vendorUserId ? (
          <AlreadyClaimedView vendorBusiness={vendorBusiness} />
        ) : (
          <ClaimViewContainer>
            <ClaimView
              onClick={continueHandler}
              submitted={submitted}
              vendorBusiness={vendorBusiness}
              email={vendorUser.email}
            />
          </ClaimViewContainer>
        )}
      </LoginRegistrationLayout>
    </ConditionalNavWrapper>
  );
}

AlreadyClaimedView.propTypes = {
  vendorBusiness: PropTypes.shape({ id: PropTypes.string }).isRequired,
};

ClaimView.propTypes = {
  onClick: PropTypes.func,
  submitted: PropTypes.bool,
  vendorBusiness: PropTypes.object,
  email: PropTypes.string,
};

ClaimView.defaultProps = {
  onClick: null,
  submitted: false,
  vendorBusiness: null,
  email: null,
}

export default ExistingBusinessRedirect;
